if (!customElements.get('cinq-availability')) {
  class CinqAvailability extends HTMLElement {
    static LOW_STOCK_THRESHOLD = 5;
    static SHOW_QUANTITY = false;

    variantChangeUnsubscriber = undefined;
    variantInputChangeHandler = undefined;
    variants = undefined;
    abortController = undefined;

    connectedCallback() {
      this.variants = this.parseVariants();
      this.check();

      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        this.onVariantChange(event);
      });

      const variantInput = this.getVariantInput();
      if (variantInput) {
        this.variantInputChangeHandler = () => this.check();
        variantInput.addEventListener('change', this.variantInputChangeHandler);
      }
    }

    disconnectedCallback() {
      if (this.variantChangeUnsubscriber) {
        this.variantChangeUnsubscriber();
      }

      const variantInput = this.getVariantInput();
      if (variantInput && this.variantInputChangeHandler) {
        variantInput.removeEventListener('change', this.variantInputChangeHandler);
      }

      this.abortController?.abort();
    }

    get strings() {
      return window.availabilityBadgeStrings || {};
    }

    get labelEl() {
      return this.querySelector('.cinq-availability__label');
    }

    get availableText() {
      const template = this.querySelector('template[data-text-available]');
      return template?.innerHTML.trim() || this.strings.available || '';
    }

    get unavailableText() {
      const template = this.querySelector('template[data-text-unavailable]');
      return template?.innerHTML.trim() || this.strings.restock || '';
    }

    getProductForm() {
      const root = this.closest('assorted-product, product-info');
      return root?.querySelector('form[action*="/cart/add"]') || document.querySelector('form[action*="/cart/add"]');
    }

    getVariantInput() {
      const form = this.getProductForm();
      return (
        form?.querySelector('[data-variant-id="product-variant-id"]') ||
        form?.querySelector('input[name="id"]') ||
        form?.querySelector('input[name="items[][id]"]')
      );
    }

    formatCountString(template, quantity) {
      if (!template) {
        return '';
      }

      return template.replaceAll('[quantity]', quantity).replaceAll('{{ quantity }}', quantity);
    }

    parseVariants() {
      const script = this.querySelector('script[data-variants]');

      if (!script) {
        return {};
      }

      try {
        return JSON.parse(script.textContent).reduce((map, { id, ...variant }) => {
          map[String(id)] = variant;
          return map;
        }, {});
      } catch {
        return {};
      }
    }

    onVariantChange(event) {
      const { sectionId, variant } = event.data || {};
      const productInfo = this.closest('product-info');

      if (productInfo && sectionId && productInfo.dataset.section !== sectionId) {
        return;
      }

      if (variant?.id) {
        this.dataset.variantId = String(variant.id);
      }

      this.check();
    }

    unavailableLabel(variantId) {
      const { restock } = this.variants[String(variantId)] || {};

      if (restock) {
        return `${this.unavailableText} - ${restock}`;
      }

      return this.unavailableText;
    }

    currentVariantId() {
      const input = this.getVariantInput();
      return input?.value || this.dataset.variantId || '';
    }

    dispatchAvailabilityUpdate(available) {
      this.dataset.available = available ? 'true' : 'false';
      this.dispatchEvent(
        new CustomEvent('cinq-availability:updated', {
          bubbles: true,
          detail: { available },
        })
      );
    }

    setLoading() {
      this.classList.remove('is-available', 'is-unavailable', 'is-low');
      this.classList.add('is-loading');
      this.setAttribute('aria-busy', 'true');

      if (this.labelEl) {
        this.labelEl.textContent = this.strings.loading || '';
      }

      this.hidden = false;
    }

    render(state, text) {
      this.classList.remove('is-loading', 'is-available', 'is-unavailable', 'is-low');
      this.classList.add(state);
      this.removeAttribute('aria-busy');

      if (this.labelEl) {
        this.labelEl.innerHTML = text;
      }

      this.hidden = false;
    }

    hide() {
      this.classList.remove('is-loading', 'is-available', 'is-unavailable', 'is-low');
      this.removeAttribute('aria-busy');
      this.hidden = true;
    }

    check() {
      const { appUrl, market, productId } = this.dataset;

      if (!market) {
        this.hide();
        return;
      }

      const variantId = this.currentVariantId();
      let qs = variantId ? `variant_id=${encodeURIComponent(variantId)}` : `product_id=${encodeURIComponent(productId)}`;
      qs += `&market=${encodeURIComponent(market)}`;

      this.abortController?.abort();
      this.abortController = new AbortController();
      this.setLoading();

      fetch(`${appUrl}/inventory/availability?${qs}`, {
        credentials: 'omit',
        signal: this.abortController.signal,
      })
        .then((r) => r.json())
        .then((d) => {
          if (d.reason === 'market_not_configured' || d.reason === 'no_locations_for_market') {
            this.hide();
            return;
          }

          if (d.available) {
            const { LOW_STOCK_THRESHOLD, SHOW_QUANTITY } = this.constructor;

            if (LOW_STOCK_THRESHOLD && d.quantity > 0 && d.quantity <= LOW_STOCK_THRESHOLD) {
              this.render(
                'is-low',
                SHOW_QUANTITY
                  ? this.formatCountString(this.strings.lowStockWithCount, d.quantity)
                  : this.strings.lowStock
              );
            } else {
              this.render(
                'is-available',
                SHOW_QUANTITY
                  ? this.formatCountString(this.strings.inStockWithCount, d.quantity)
                  : this.availableText
              );
            }

            this.dispatchAvailabilityUpdate(true);
          } else {
            const { preorder } = this.variants[String(variantId)] || {};

            if (preorder) {
              this.render('is-available', preorder);
              this.dispatchAvailabilityUpdate(true);
            } else {
              this.render('is-unavailable', this.unavailableLabel(variantId));
              this.dispatchAvailabilityUpdate(false);
            }
          }
        })
        .catch((error) => {
          if (error.name === 'AbortError') {
            return;
          }

          this.hide();
        });
    }
  }

  customElements.define('cinq-availability', CinqAvailability);
}
